"use client";

import React, { useEffect, useMemo, useState } from "react";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import Image from "next/image";
import Link from "next/link";
import { PlusCircle, Heart, Search, Play, Music, ImageIcon, Mail, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ---------- CONFIG ----------
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;
const supabaseClient: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ---------- STYLES (Tailwind + small inline) ----------
/*
  This component assumes Tailwind is configured in the project.
  The following custom classes are used:
  - glowing-gold: text-shadow / glow effect
  - gold-card-border, planet-anim, flame-anim etc implemented below via inline <style>
*/

type Story = {
  id: string;
  title: string;
  created_at: string;
  cover_image_url?: string | null;
  likes_count?: number | null;
  video_url?: string | null;
  music_url?: string | null;
};

export default function DashboardPage() {
  const [user, setUser] = useState<any | null>(null);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAllStories, setShowAllStories] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);
  const [videosOpen, setVideosOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [pageSize, setPageSize] = useState(4); // default visible stories
  const [maxPages, setMaxPages] = useState(1000); // per user's request we support up to 1000 pages
  const [achievementsCount, setAchievementsCount] = useState(0);

  // New state for video modal carousel and expanded mode
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);

  useEffect(() => {
    // initialize auth state & fetch stories
    let mounted = true;
    async function init() {
      setLoading(true);
      try {
        const { data: authData } = await supabaseClient.auth.getUser();
        if (!mounted) return;
        setUser(authData?.user ?? null);

        // fetch up to 1000 stories (limit to prevent insane loads)
        const limit = Math.min(1000, maxPages * pageSize);
        const { data, error } = await supabaseClient
          .from("stories")
          .select("id, title, created_at, cover_image_url, likes_count, video_url, music_url")
          .order("created_at", { ascending: false })
          .limit(limit);

        if (error) {
          console.error("Error fetching stories:", error);
          setStories([]);
        } else {
          setStories((data as Story[]) || []);
          setAchievementsCount((data as Story[]).length || 0);
        }
      } catch (err) {
        console.error("Init error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    init();
    return () => {
      mounted = false;
    };
  }, [pageSize, maxPages]);

  // Derived buckets
  const filteredStories = useMemo(() => {
    if (!search.trim()) return stories;
    const q = search.toLowerCase();
    return stories.filter((s) => (s.title ?? "").toLowerCase().includes(q));
  }, [search, stories]);

  const visibleStories = useMemo(() => {
    if (showAllStories) return filteredStories;
    return filteredStories.slice(0, pageSize);
  }, [filteredStories, showAllStories, pageSize]);

  const videos = useMemo(() => stories.filter((s) => s.video_url).map((s) => ({ id: s.id, title: s.title, url: s.video_url! })), [stories]);
  const images = useMemo(() => stories.filter((s) => s.cover_image_url).map((s) => ({ id: s.id, title: s.title, url: s.cover_image_url! })), [stories]);
  const musics = useMemo(() => stories.filter((s) => s.music_url).map((s) => ({ id: s.id, title: s.title, url: s.music_url! })), [stories]);

  // Achievement level calculation
  function calcLevel(count: number) {
    if (count >= 500) return { level: "Master Storyteller", rank: 5, icon: "★ ★ ★ ★ ★" };
    if (count >= 250) return { level: "Legend", rank: 4, icon: "★ ★ ★ ★" };
    if (count >= 100) return { level: "Expert", rank: 3, icon: "★ ★ ★" };
    if (count >= 25) return { level: "Creator", rank: 2, icon: "★ ★" };
    return { level: "Beginner", rank: 1, icon: "★" };
  }

  const achievement = calcLevel(achievementsCount);

  // Small helpers
  const openGalleryAt = (index: number) => {
    setSelectedGalleryIndex(index);
    setGalleryOpen(true);
  };

  // When opening videos modal, ensure currentVideoIndex is valid (default 0)
  useEffect(() => {
    if (videosOpen) {
      // clamp currentVideoIndex
      setCurrentVideoIndex((idx) => {
        if (!videos || videos.length === 0) return 0;
        if (idx < 0 || idx >= videos.length) return 0;
        return idx;
      });
      setIsVideoExpanded(false);
    }
  }, [videosOpen, videos.length]);

  // keyboard navigation for videos modal
  useEffect(() => {
    if (!videosOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        prevVideo();
      } else if (e.key === "ArrowRight") {
        nextVideo();
      } else if (e.key === "Escape") {
        setVideosOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videosOpen, currentVideoIndex, videos.length]);

  function prevVideo() {
    if (videos.length === 0) return;
    setCurrentVideoIndex((i) => (i - 1 + videos.length) % videos.length);
  }
  function nextVideo() {
    if (videos.length === 0) return;
    setCurrentVideoIndex((i) => (i + 1) % videos.length);
  }

  // ---------- RENDER ----------
  return (
    <div className="min-h-screen bg-black text-slate-100 antialiased">
      <style>{`
        .glowing-gold {
          color: #ffd86b;
          text-shadow:
            0 0 6px rgba(255,215,0,0.45),
            0 2px 18px rgba(255,180,0,0.12);
        }
        .gold-border {
          box-shadow: 0 0 18px rgba(255, 200, 50, 0.08), inset 0 0 14px rgba(255,180,0,0.03);
        }
        .gold-accent {
          background: linear-gradient(90deg, rgba(255,215,102,0.06), rgba(255,183,0,0.02));
          border: 1px solid rgba(255,190,60,0.06);
        }
        /* planet animation */
        .planet-anim {
          position: absolute;
          width: 220px;
          height: 220px;
          border-radius: 9999px;
          background: radial-gradient(circle at 30% 30%, rgba(255,210,120,0.06), rgba(0,0,0,0));
          filter: blur(28px);
          transform: translate3d(0,0,0);
          animation: floaty 13s linear infinite;
          z-index: 0;
        }
        @keyframes floaty {
          0% { transform: translateY(0) translateX(0) rotate(0deg); }
          50% { transform: translateY(-18px) translateX(12px) rotate(10deg); }
          100% { transform: translateY(0) translateX(0) rotate(0deg); }
        }
        /* flame flicker */
        .flame-anim::after {
          content: "";
          position: absolute;
          width: 320px;
          height: 60px;
          left: -40px;
          top: -18px;
          background: radial-gradient(circle at 50% 50%, rgba(255,190,60,0.06), transparent 30%);
          filter: blur(10px);
          animation: flicker 2.6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes flicker {
          0% { opacity: 0.6; transform: translateY(0) scaleX(1); }
          50% { opacity: 0.9; transform: translateY(-6px) scaleX(1.05); }
          100% { opacity: 0.6; transform: translateY(0) scaleX(1); }
        }
        .card-glow {
          background: linear-gradient(180deg, rgba(255,215,102,0.018), rgba(0,0,0,0.2));
          border: 1px solid rgba(255,200,60,0.06);
          transition: transform 0.28s cubic-bezier(.2,.9,.3,1), box-shadow 0.28s;
        }
        .card-glow:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 0 10px 40px rgba(255,180,0,0.06), 0 0 30px rgba(255,200,60,0.04);
        }
      `}</style>

      {/* top area */}
      <div className="relative overflow-hidden">
        <div className="planet-anim left-8 top-8" />
        <div className="planet-anim right-8 top-24" style={{ width: 300, height: 300 }} />
        <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full gold-accent flex items-center justify-center gold-border shadow-lg flame-anim">
                <div className="text-black bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full px-2 py-1 font-bold">TS</div>
              </div>
              <div>
                <h1 className="text-4xl md:text-6xl font-serif font-extrabold glowing-gold leading-tight">
                  The Story Club
                </h1>
                <p className="text-sm text-slate-300 mt-1">AI-generated stories • images • music • videos — premium creative studio</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link href="/dashboard/new-story" className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold glowing-gold border border-transparent bg-gradient-to-r from-yellow-500/6 to-yellow-600/6 hover:from-yellow-500/10 hover:scale-105 transition transform">
                <PlusCircle className="h-5 w-5" /> New Story
              </Link>
            
            </div>
          </header>

          <section className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left: Main content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Search + controls */}
              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <div className="flex items-center gap-2 flex-1 card-glow px-4 py-3 rounded-2xl">
                  <Search className="w-5 h-5 text-yellow-300/80" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search your stories (title) — try 'adventure' ..."
                    className="bg-transparent outline-none text-slate-200 placeholder:text-slate-400 text-sm flex-1"
                  />
                  <div className="text-xs text-slate-400">{filteredStories.length} results</div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowAllStories((s) => !s); }}
                    className="px-4 py-3 rounded-2xl card-glow text-sm"
                  >
                    {showAllStories ? "Show Less" : `Show ${pageSize} (expand)`}
                  </button>
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="px-3 py-2 rounded-2xl bg-slate-900">
                    <option value={4}>4</option>
                    <option value={8}>8</option>
                    <option value={16}>16</option>
                    <option value={32}>32</option>
                  </select>
                </div>
              </div>

              {/* Stories Section */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold glowing-gold">Your Stories</h2>
                  <div className="text-sm text-slate-400">Stories are expandable and searchable — click to open</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
                  {loading ? (
                    <div className="col-span-full py-12 text-center text-slate-400">Loading stories...</div>
                  ) : visibleStories.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400">
                      No stories found. <Link href="/dashboard/new-story" className="text-yellow-400 underline">Create your first story</Link>
                    </div>
                  ) : (
                    visibleStories.map((story) => (
                      <motion.div
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        key={story.id}
                        className="relative rounded-2xl p-4 bg-gradient-to-b from-slate-900/40 to-slate-900/10 gold-border card-glow overflow-hidden"
                      >
                        <div className="absolute inset-0 pointer-events-none opacity-10 bg-gradient-to-br from-amber-500/2 to-transparent" />
                        <div className="flex gap-4">
                          <div className="w-28 h-20 rounded-lg overflow-hidden border border-slate-700 relative">
                            {story.cover_image_url ? (
                              // Next/Image layout changed to fill compatibility
                              <Image src={story.cover_image_url} alt={story.title || "cover"} fill style={{ objectFit: "cover" }} />
                            ) : (
                              <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500">No Cover</div>
                            )}
                          </div>

                          <div className="flex-1">
                            <h3 className="text-lg font-semibold leading-tight">{story.title}</h3>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <div>Created: {new Date(story.created_at).toLocaleDateString()}</div>
                              <div className="flex items-center gap-1"><Heart className="w-4 h-4 text-red-400" /> {story.likes_count ?? 0}</div>
                            </div>

                            <div className="mt-3 flex items-center gap-2">
                              <Link href={`/dashboard/story/${story.id}`} className="px-3 py-2 text-sm rounded-md bg-yellow-500/6 glowing-gold font-medium">
                                Open
                              </Link>

                              {story.video_url ? (
                                <button className="px-3 py-2 rounded-md bg-slate-800 text-sm" onClick={() => { setVideosOpen(true); }}>
                                  <Play className="w-4 h-4 inline-block mr-1 text-yellow-400" /> Play Video
                                </button>
                              ) : null}

                              {story.music_url ? (
                                <button className="px-3 py-2 rounded-md bg-slate-800 text-sm" onClick={() => setMusicOpen(true)}>
                                  <Music className="w-4 h-4 inline-block mr-1 text-yellow-400" /> Play Music
                                </button>
                              ) : null}

                              <button className="px-3 py-2 rounded-md bg-slate-800 text-sm" onClick={() => {
                                const idx = images.findIndex((im) => im.id === story.id);
                                if (idx >= 0) openGalleryAt(idx);
                                else if (story.cover_image_url) openGalleryAt(images.length);
                              }}>
                                <ImageIcon className="w-4 h-4 inline-block mr-1 text-yellow-400" /> View Image
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>

                {!showAllStories && filteredStories.length > pageSize && (
                  <div className="pt-4 text-center">
                    <button onClick={() => setShowAllStories(true)} className="px-6 py-3 rounded-full glowing-gold border border-yellow-600/10 bg-slate-900/40">
                      Show all {filteredStories.length} stories
                    </button>
                  </div>
                )}
              </section>

              {/* Videos Section */}
              <section className="mt-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold glowing-gold">Videos</h2>
                  <div className="text-sm text-slate-400">{videos.length} video(s) found</div>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.length === 0 && <div className="text-slate-400 col-span-full py-6">No videos generated yet.</div>}
                  {videos.slice(0, 6).map((v, i) => (
                    <motion.div key={v.id + i} whileHover={{ scale: 1.02 }} className="rounded-xl p-3 bg-slate-900/60 gold-border card-glow">
                      <div className="relative aspect-video rounded-md overflow-hidden bg-black">
                        {/* If the video url is a direct video file, use <video>, else show a play thumbnail */}
                        {v.url.endsWith(".mp4") || v.url.includes("blob") ? (
                          <video src={v.url} controls className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <Play className="w-10 h-10 text-yellow-400" />
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="text-sm">{v.title}</div>
                        <a href={v.url} target="_blank" rel="noreferrer" className="text-xs underline text-yellow-300">Open</a>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {videos.length > 6 && (
                  <div className="mt-3 text-center">
                    <button onClick={() => setVideosOpen((s) => !s)} className="px-4 py-2 rounded-full">
                      {videosOpen ? "Close video pane" : `Show all ${videos.length} videos`}
                    </button>
                  </div>
                )}
              </section>

              {/* Image Gallery */}
              <section className="mt-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold glowing-gold">Image Gallery</h2>
                  <div className="text-sm text-slate-400">{images.length} image(s)</div>
                </div>

                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {images.length === 0 && <div className="text-slate-400 col-span-full py-6">No images yet.</div>}
                  {images.slice(0, 12).map((img, idx) => (
                    <button key={img.id} onClick={() => openGalleryAt(idx)} className="relative rounded-lg overflow-hidden h-40 bg-slate-800/30 gold-border">
                      <Image src={img.url} alt={img.title} fill style={{ objectFit: "cover" }} />
                      <div className="absolute bottom-2 left-2 right-2 text-xs text-slate-100/90 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-md flex items-center justify-between">
                        <div className="truncate">{img.title}</div>
                        <div className="text-yellow-300 text-sm"><Star className="w-3 h-3 inline-block mr-1" /> </div>
                      </div>
                    </button>
                  ))}
                </div>

                {images.length > 12 && (
                  <div className="mt-3 text-center">
                    <button onClick={() => setGalleryOpen(true)} className="px-4 py-2 rounded-full">
                      Open full gallery ({images.length})
                    </button>
                  </div>
                )}
              </section>

              {/* Music */}
              <section className="mt-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold glowing-gold">Music</h2>
                  <div className="text-sm text-slate-400">{musics.length} track(s)</div>
                </div>

                <div className="mt-3 space-y-3">
                  {musics.length === 0 && <div className="text-slate-400">No music generated yet.</div>}
                  {musics.slice(0, 6).map((m, i) => (
                    <div key={m.url + i} className="rounded-xl p-3 bg-slate-900/50 gold-border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-md bg-slate-800/40 flex items-center justify-center">
                          <Music className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">{m.title}</div>
                          <div className="text-xs text-slate-400">From a story</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <audio controls src={m.url} className="h-8" />
                        <a href={m.url} target="_blank" rel="noreferrer" className="text-xs underline text-yellow-300">Open</a>
                      </div>
                    </div>
                  ))}
                </div>

                {musics.length > 6 && (
                  <div className="mt-3 text-center">
                    <button onClick={() => setMusicOpen((s) => !s)} className="px-4 py-2 rounded-full">
                      {musicOpen ? "Close" : `Show all ${musics.length}`}
                    </button>
                  </div>
                )}
              </section>

              {/* Contact + footer CTA */}
              <div className="mt-8 rounded-2xl p-6 gold-border bg-gradient-to-b from-slate-900/30 to-black/10 card-glow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold glowing-gold">Need help or want to partner?</div>
                    <div className="text-sm text-slate-400 mt-1">Contact our team — we love feedback and collaborations.</div>
                  </div>
                  <div>
                    <Link href="/contact" className="px-5 py-3 rounded-full bg-yellow-500/8 glowing-gold">Contact Us</Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: New story, Achievements */}
            <aside className="lg:col-span-1 space-y-6">
              <div className="rounded-2xl p-4 gold-border card-glow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-400">Create faster with AI</div>
                    <h3 className="text-lg font-semibold glowing-gold">New Story (AI)</h3>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-slate-300">Generate a new story, cover image, music, or short video automatically. Use presets or customize your prompt.</p>
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <Link href="/dashboard/new-story" className="px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/6 to-yellow-600/6 glowing-gold text-center">
                      Quick AI Story
                    </Link>
                    <Link href="/dashboard/new-story" className="px-4 py-2 rounded-full bg-slate-800 text-center">Advanced Creator</Link>
                  </div>

                  <div className="mt-4 text-xs text-slate-400">
                    Tip: try "space pirate noir" or "romantic comedy in Lagos" in prompts.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl p-4 gold-border card-glow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-slate-400">Achievements</div>
                    <h3 className="text-lg font-semibold glowing-gold">{achievement.level}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-yellow-300">{achievement.icon}</div>
                    <div className="text-xs text-slate-400">{achievementsCount} stories</div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                    {/* progress bar toward next meaningful threshold */}
                    {(() => {
                      const thresholds = [1, 5, 25, 100, 250, 500];
                      let next = thresholds.find((t) => t > achievementsCount) ?? 500;
                      let prev = thresholds.slice().reverse().find((t) => t <= achievementsCount) ?? 0;
                      const pct = Math.min(100, Math.round(((achievementsCount - prev) / Math.max(1, next - prev)) * 100));
                      return <div className="h-full bg-gradient-to-r from-yellow-400 to-amber-500" style={{ width: `${pct}%` }} />;
                    })()}
                  </div>

                  <div className="mt-3 text-xs text-slate-400">
                    Level up by creating more stories — unlock premium badges and exclusive prompts.
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="p-3 bg-slate-900/40 rounded-lg text-center">
                      <div className="text-lg font-bold">{achievementsCount >= 1 ? "✓" : "—"}</div>
                      <div className="text-xs text-slate-400 mt-1">First Story</div>
                    </div>
                    <div className="p-3 bg-slate-900/40 rounded-lg text-center">
                      <div className="text-lg font-bold">{achievementsCount >= 25 ? "✓" : "—"}</div>
                      <div className="text-xs text-slate-400 mt-1">25 Stories</div>
                    </div>
                    <div className="p-3 bg-slate-900/40 rounded-lg text-center">
                      <div className="text-lg font-bold">{achievementsCount >= 100 ? "✓" : "—"}</div>
                      <div className="text-xs text-slate-400 mt-1">100 Stories</div>
                    </div>
                  </div>
                </div>
              </div>

       
            
            </aside>
          </section>
        </div>
      </div>

      {/* Gallery Modal (scrollable, max z-index) */}
      <AnimatePresence>
        {galleryOpen && images.length > 0 && (
          <motion.div
            key="gallery-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-6"
            style={{ zIndex: 99999 }}
          >
            <div onClick={() => setGalleryOpen(false)} className="absolute inset-0 bg-black/70" />
            <motion.div
              initial={{ scale: 0.96, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.98, y: 12 }}
              className="relative w-full max-w-6xl bg-slate-900/80 rounded-2xl p-4 gold-border"
              style={{ maxHeight: "90vh", overflow: "auto" }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg glowing-gold">Gallery — {images.length} images</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setGalleryOpen(false)} className="px-3 py-1 rounded-md">Close</button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {images.map((img, idx) => (
                  <div key={img.id + idx} className="rounded-lg overflow-hidden relative h-72 bg-slate-800/30">
                    <Image src={img.url} alt={img.title} fill style={{ objectFit: "cover" }} />
                    <div className="absolute bottom-2 left-2 right-2 text-xs text-slate-100/90 bg-black/40 px-2 py-1 rounded-md flex items-center justify-between">
                      <div className="truncate">{img.title}</div>
                      <a href={img.url} target="_blank" rel="noreferrer" className="text-yellow-300 text-xs underline">Open</a>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Videos Modal (carousel-style, scrollable, high z-index) */}
      <AnimatePresence>
        {videosOpen && (
          <motion.div
            key="videos-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-6"
            style={{ zIndex: 99999 }}
          >
            <div onClick={() => setVideosOpen(false)} className="absolute inset-0 bg-black/80" />
            <motion.div
              initial={{ y: 20, scale: 0.98 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 6, scale: 0.98 }}
              className={`relative z-50 w-full bg-slate-900 rounded-2xl p-4 gold-border`}
              style={{
                maxWidth: isVideoExpanded ? "calc(100% - 48px)" : "900px",
                width: "100%",
                maxHeight: "92vh",
                overflow: "auto",
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg glowing-gold">All Videos</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setIsVideoExpanded((s) => !s)} className="px-3 py-1 rounded-md">
                    {isVideoExpanded ? "Compact" : "Expand"}
                  </button>
                  <button onClick={() => setVideosOpen(false)} className="px-3 py-1 rounded-md">Close</button>
                </div>
              </div>

              {/* If there are no videos */}
              {videos.length === 0 ? (
                <div className="text-slate-400 py-6">No videos available.</div>
              ) : (
                <div className={`flex flex-col ${isVideoExpanded ? "gap-4" : "gap-3"}`}>
                  {/* Carousel area */}
                  <div className={`w-full flex flex-col md:flex-row gap-4 items-start`}>
                    {/* Video player column */}
                    <div className="relative w-full md:w-2/3 bg-black rounded-md overflow-hidden">
                      {/* left arrow */}
                      <button
                        onClick={prevVideo}
                        aria-label="Previous video"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-2 z-20"
                      >
                        ◀
                      </button>

                      {/* right arrow */}
                      <button
                        onClick={nextVideo}
                        aria-label="Next video"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-2 z-20"
                      >
                        ▶
                      </button>

                      <div style={{ aspectRatio: "16 / 9" }} className="w-full h-full flex items-center justify-center bg-black">
                        {/* Show the selected video; if mp4 render <video> else show play icon */}
                        {videos[currentVideoIndex].url.endsWith(".mp4") || videos[currentVideoIndex].url.includes("blob") ? (
                          // when compact, keep controls visible; when expanded, allow bigger player
                          <video
                            controls
                            src={videos[currentVideoIndex].url}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <Play className="w-16 h-16 text-yellow-400" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details column */}
                    <div className="w-full md:w-1/3">
                      <div className="rounded-md p-3 bg-slate-900/60 gold-border">
                        <div className="text-sm text-slate-400">Video {currentVideoIndex + 1} of {videos.length}</div>
                        <div className="text-lg font-semibold mt-2">{videos[currentVideoIndex].title}</div>
                        <div className="mt-3 text-xs text-slate-400">
                          Click arrows or use ← / → keys to navigate. The video plays in the left panel — details remain visible here.
                        </div>

                        <div className="mt-4 flex gap-2">
                          <a
                            href={videos[currentVideoIndex].url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 rounded-md bg-yellow-500/8 glowing-gold text-sm"
                          >
                            Open Full Video
                          </a>

                          <button
                            onClick={() => {
                              // open raw in new tab as a "full" option (also keep modal)
                              window.open(videos[currentVideoIndex].url, "_blank", "noopener,noreferrer");
                            }}
                            className="px-3 py-2 rounded-md bg-slate-800 text-sm"
                          >
                            Open Raw
                          </button>

                          <button
                            onClick={nextVideo}
                            className="px-3 py-2 rounded-md bg-slate-800 text-sm"
                          >
                            Next ▶
                          </button>
                        </div>

                        <div className="mt-4 text-xs text-slate-400">
                          Tip: Use Expand to get a larger player while keeping context on-screen.
                        </div>
                      </div>

                      {/* small thumbnails / quick nav */}
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {videos.map((v, idx) => (
                          <button
                            key={v.id + idx}
                            onClick={() => setCurrentVideoIndex(idx)}
                            className={`rounded-md overflow-hidden relative h-20 bg-slate-800/30 border ${idx === currentVideoIndex ? "border-yellow-400" : "border-transparent"}`}
                          >
                            {/* For thumbnails we can't generate a true video poster reliably; show the play icon centered */}
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-yellow-300" />
                            </div>
                            <div className="absolute left-1 top-1 text-xs text-slate-100/90 bg-black/40 px-1 rounded">{idx + 1}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* list of other videos (compact) shown below when expanded enough (keeps modal scrollable) */}
                  <div className="mt-3 space-y-2">
                    {videos.map((v, i) => (
                      <div
                        key={v.id + i}
                        className={`rounded-lg overflow-hidden bg-slate-900/50 p-2 flex items-center justify-between ${i === currentVideoIndex ? "ring-2 ring-yellow-400" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setCurrentVideoIndex(i)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-12 bg-black rounded-md flex items-center justify-center">
                            <Play className="w-6 h-6 text-yellow-400" />
                          </div>
                          <div>
                            <div className="text-sm">{v.title}</div>
                            <div className="text-xs text-slate-400">Video #{i + 1}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={v.url} target="_blank" rel="noreferrer" className="text-xs underline text-yellow-300">Open</a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Music Modal */}
      <AnimatePresence>
        {musicOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 flex items-center justify-center p-6">
            <div onClick={() => setMusicOpen(false)} className="absolute inset-0 bg-black/80" />
            <motion.div initial={{ y: 20, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 6, scale: 0.98 }} className="relative z-50 w-full max-w-2xl bg-slate-900 rounded-2xl p-4 gold-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg glowing-gold">Music Tracks</h3>
                <button onClick={() => setMusicOpen(false)} className="px-3 py-1 rounded-md">Close</button>
              </div>
              <div className="space-y-3">
                {musics.map((m, i) => (
                  <div key={m.url + i} className="rounded-lg overflow-hidden bg-slate-900/50 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm">{m.title}</div>
                      <div className="text-xs text-slate-400">From story</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <audio controls src={m.url} />
                      <a href={m.url} target="_blank" rel="noreferrer" className="text-xs underline text-yellow-300">Open</a>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
